import { useEffect, useRef, useState } from "react";
import { Icon } from "./Icon";

const PASSWORD =
  (import.meta.env.VITE_ARCHIVE_PASSWORD as string) || "12345678!";
const STORAGE_KEY = "bp26.archive.unlocked";

interface Props {
  children: React.ReactNode;
  onCancel: () => void;
}

export function ArchivePasswordGate({ children, onCancel }: Props) {
  const [unlocked, setUnlocked] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [input, setInput] = useState("");
  const [error, setError] = useState(false);
  const [show, setShow] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!unlocked) inputRef.current?.focus();
  }, [unlocked]);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (input === PASSWORD) {
      try {
        sessionStorage.setItem(STORAGE_KEY, "1");
      } catch {
        /* ignore */
      }
      setUnlocked(true);
      setError(false);
    } else {
      setError(true);
      setInput("");
      setTimeout(() => setError(false), 700);
      inputRef.current?.focus();
    }
  }

  if (unlocked) return <>{children}</>;

  return (
    <div className="min-h-full safe-top safe-bottom flex flex-col px-6">
      <header className="pt-6 flex items-center justify-between">
        <button
          onClick={onCancel}
          aria-label="Terug naar overzicht"
          className="inline-flex items-center gap-2 text-zinc-400 hover:text-zinc-200 text-2xs uppercase font-semibold"
          style={{ letterSpacing: "0.18em" }}
        >
          <span aria-hidden="true">←</span>
          Terug
        </button>
        <div className="flex items-center gap-2.5">
          <span className="w-1 h-5 bg-accent rounded-full" />
          <span
            className="text-accent text-[10px] font-semibold uppercase"
            style={{ letterSpacing: "0.2em" }}
          >
            BP26 · ARCHIEF
          </span>
        </div>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center max-w-sm mx-auto w-full">
        <div
          className="w-12 h-12 rounded-full mb-5 flex items-center justify-center"
          style={{
            background: "rgba(94,234,212,0.08)",
            border: "1px solid rgba(94,234,212,0.22)",
          }}
        >
          <Icon.Lock className="w-5 h-5 text-accent" />
        </div>

        <h1 className="text-zinc-100 text-2xl font-semibold tracking-tight mb-2 text-center">
          Beveiligde sessie
        </h1>
        <p className="text-zinc-500 text-sm leading-relaxed text-center mb-8">
          Het BP26-dossier bevat operationele meldingen van 5 mei 2026.
          <br />
          Vraag het wachtwoord aan de CPO.
        </p>

        <form onSubmit={submit} className="w-full">
          <div
            className={`relative rounded-2xl flex items-center transition-all ${
              error ? "animate-pulse" : ""
            }`}
            style={{
              background: "rgba(255,255,255,0.025)",
              border: error
                ? "1px solid rgba(239,68,68,0.4)"
                : "1px solid rgba(255,255,255,0.06)",
            }}
          >
            <input
              ref={inputRef}
              type={show ? "text" : "password"}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              placeholder="Wachtwoord"
              aria-label="Wachtwoord voor BP26-archief"
              aria-invalid={error}
              className="flex-1 bg-transparent h-14 px-4 text-base text-zinc-100 placeholder:text-zinc-600 outline-none rounded-2xl"
              style={{ fontFeatureSettings: '"tnum"' }}
            />
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              className="px-4 text-2xs uppercase font-semibold text-zinc-500 hover:text-zinc-300"
              style={{ letterSpacing: "0.18em" }}
              aria-label={show ? "Verberg wachtwoord" : "Toon wachtwoord"}
            >
              {show ? "Verberg" : "Toon"}
            </button>
          </div>

          <button
            type="submit"
            disabled={!input}
            className="mt-3 w-full h-12 rounded-full bg-accent text-ink-950 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.99]"
            style={{
              transition: "all 300ms cubic-bezier(0.32,0.72,0,1)",
            }}
          >
            Open dossier
          </button>
        </form>

        <p className="text-2xs text-zinc-600 mt-6 text-center max-w-xs">
          Sessie blijft open zolang deze tab open blijft.
        </p>
      </main>

      <footer className="pb-6 text-center">
        <p className="text-2xs text-zinc-700 italic">Vakwerk verdient bewijs.</p>
      </footer>
    </div>
  );
}
